import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { pushParams } from 'dpl/actions/query_params';
import Icon from 'dpl/common/components/Icon';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import {
  FILTER_POSTAL_CODE,
  PAGE,
  RESULTS_TYPE
} from 'dpl/constants/query_params';
import { isMobileUA, flatten } from 'dpl/shared/utils';
import { sendRudderstackEvent } from 'dpl/util/analytics';

import { RESULTS_TYPES } from '../utils/constants';
import FartherBreedersBanner from './FartherBreedersBanner';
import NearbyBreedersModule from './NearbyBreedersModule';

const IS_MOBILE = isMobileUA();

const RESULT_GROUP_SIZE = 8;

function FewPuppiesMessage({ scrollToTab }) {
  const dispatch = useDispatch();

  function handleClick() {
    dispatch(
      pushParams({ [RESULTS_TYPE]: RESULTS_TYPES.BREEDERS }, { hash: true })
    );

    scrollToTab();
  }

  return (
    <div className="FewPuppiesMessage flex items-center flex-column tc w-100 pt8 mb20">
      <Icon
        className="black mb4"
        name="puppy-search"
        height="56px"
        width="66px"
      />
      <p className="b">Not finding your perfect pup?</p>
      <p className="pb4">
        Explore and connect with Good Dog breeders to learn about upcoming
        litters.
      </p>
      <button
        type="button"
        onClick={handleClick}
        className="button button--medium button--primary"
      >
        Explore Good Dog breeders
      </button>
    </div>
  );
}

FewPuppiesMessage.propTypes = {
  scrollToTab: PropTypes.func.isRequired
};

function flattenDeliveryMethods(deliveryMethods) {
  return flatten(Object.values(deliveryMethods || {})).map(
    method => method.name
  );
}

export function resultGroupViewProps(
  breedId,
  resultsType,
  result,
  position,
  page
) {
  let resultProps = {};
  if (resultsType === RESULTS_TYPES.PUPPIES) {
    resultProps = {
      dog_id: result.id,
      puppy_name: result.name,
      breeder_profile_id: result.breeder_profile.id,
      breed_id: breedId,
      miles_away: result.distance_in_miles,
      miles_away_ranking: result.ranking_distance_in_miles,
      transportation_options: flattenDeliveryMethods(
        result.breeder_profile.delivery_methods
      )
    };
  } else {
    resultProps = {
      breeder_profile_id: result.id,
      breed_id: breedId,
      miles_away: result.distance_in_miles,
      transportation_options: flattenDeliveryMethods(result.delivery_methods)
    };
  }

  return {
    ...resultProps,
    position,
    page_number: page
  };
}

function SearchResultsItem({
  result,
  results,
  resultsType,
  position,
  positionOnPage,
  analyticsPageNumber,
  hideDistance,
  Component
}) {
  const breedId = useSelector(({ server }) => server.breedId);

  const analyticsView =
    resultsType === RESULTS_TYPES.PUPPIES
      ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
      : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS;

  const analyticsViewFeature =
    resultsType === RESULTS_TYPES.PUPPIES
      ? ANALYTICS_VIEW_FEATURES.PUPPY_CARD
      : ANALYTICS_VIEW_FEATURES.BREEDER_CARD;

  const analytics = useMemo(
    () => ({
      page_number: analyticsPageNumber,
      position,
      view: analyticsView,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS
    }),
    [position, analyticsPageNumber, analyticsView]
  );

  const trackInView = useCallback(() => {
    if (positionOnPage % RESULT_GROUP_SIZE === 1) {
      const idx = results.indexOf(result);
      const resultGroup = results.slice(idx, idx + RESULT_GROUP_SIZE);

      sendRudderstackEvent(
        ANALYTICS_EVENTS.SEARCH_RESULT_GROUP_VIEWED_BY_USER,
        {
          view: analyticsView,
          view_feature: analyticsViewFeature,
          view_feature_location:
            ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS,
          results: resultGroup.map((r, groupIdx) =>
            resultGroupViewProps(
              breedId,
              resultsType,
              r,
              position + groupIdx,
              analyticsPageNumber
            )
          )
        }
      );
    }
  }, [
    results,
    positionOnPage,
    analyticsView,
    analyticsViewFeature,
    position,
    analyticsPageNumber,
    breedId,
    resultsType
  ]);

  return (
    <Component
      result={result}
      analytics={analytics}
      hideDistance={hideDistance}
      trackInView={trackInView}
    />
  );
}

SearchResultsItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  results: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  result: PropTypes.object.isRequired,
  resultsType: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  positionOnPage: PropTypes.number.isRequired,
  analyticsPageNumber: PropTypes.number.isRequired,
  hideDistance: PropTypes.bool.isRequired,
  Component: PropTypes.func.isRequired
};

export default function SearchResults({
  scrollToTab,
  results,
  resultsType,
  Card,
  distanceInMiles,
  totalItemCount,
  className,
  analyticsPageNumber
}) {
  const {
    itemsPerPage,
    stateLander,
    citySlug,
    preappliedFilters: { stateSlug } = {}
  } = useSelector(({ server }) => server);

  const params = useSelector(({ queryParams }) => queryParams);
  const currentPage = Number(params[PAGE]) || 1;
  const filteredByLocation = Boolean(params[FILTER_POSTAL_CODE] || stateSlug);

  const isFirstPage = currentPage === 1;

  const showFewPuppiesMessage =
    isFirstPage && results.length < 4 && resultsType === RESULTS_TYPES.PUPPIES;

  function getPosition(idx) {
    let position = idx + 1;
    if (currentPage > 1) {
      position += (currentPage - 1) * itemsPerPage;
    }

    return position;
  }

  return (
    <ul
      className={classnames('SearchResults SearchResults--v2 row', className)}
    >
      {results?.map((result, idx) => {
        const positionOnPage = idx + 1;

        const isFirstResult = idx === 0 && isFirstPage;
        const prevResult = results[idx - 1] || {};

        const resultStateSlug =
          resultsType === RESULTS_TYPES.PUPPIES
            ? result?.breeder_profile?.state_slug
            : result?.state_slug;

        const didStartInRange =
          isFirstResult || prevResult.distance_in_miles <= distanceInMiles;

        const isInRange = result.distance_in_miles <= distanceInMiles;
        const isInState = resultStateSlug === stateSlug;

        const didSwitchToOutOfRange =
          !stateLander && distanceInMiles && didStartInRange && !isInRange;

        // only hide if a distance limit is specified and it goes past that distance
        const shouldHideDistanceInPuppyCards = stateLander
          ? !isInState
          : !isInRange;

        const shouldDisplayFartherBreedersBanner =
          resultsType === RESULTS_TYPES.BREEDERS && filteredByLocation
            ? Boolean(
                !result.snoozed &&
                  !stateLander &&
                  !citySlug &&
                  distanceInMiles &&
                  didSwitchToOutOfRange
              )
            : false;

        const remainingResultsCount =
          totalItemCount - ((currentPage - 1) * itemsPerPage + idx);

        const shouldDisplayNearbyBreedersModule =
          IS_MOBILE &&
          resultsType === RESULTS_TYPES.PUPPIES &&
          isFirstPage &&
          idx === 8;

        return (
          <Fragment key={result.id}>
            {shouldDisplayFartherBreedersBanner && (
              <li className="mb8 mb10-md col-12">
                <FartherBreedersBanner count={remainingResultsCount} />
              </li>
            )}
            <li
              className={classnames('mb10-md col-12 col-md-6 mb6 mb8-md', {
                'col-xl-3': resultsType === RESULTS_TYPES.PUPPIES
              })}
            >
              <SearchResultsItem
                result={result}
                resultsType={resultsType}
                results={results}
                position={getPosition(idx)}
                positionOnPage={positionOnPage}
                analyticsPageNumber={analyticsPageNumber}
                hideDistance={shouldHideDistanceInPuppyCards}
                Component={Card}
              />
            </li>
            {showFewPuppiesMessage && idx === results.length - 1 && (
              <FewPuppiesMessage scrollToTab={scrollToTab} />
            )}
            {shouldDisplayNearbyBreedersModule && <NearbyBreedersModule />}
          </Fragment>
        );
      })}
    </ul>
  );
}

SearchResults.propTypes = {
  className: PropTypes.string,
  results: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  resultsType: PropTypes.oneOf(Object.values(RESULTS_TYPES)).isRequired,
  Card: PropTypes.func.isRequired,
  distanceInMiles: PropTypes.number,
  totalItemCount: PropTypes.number,
  scrollToTab: PropTypes.func.isRequired,
  analyticsPageNumber: PropTypes.number.isRequired
};

SearchResults.defaultProps = {
  distanceInMiles: null,
  totalItemCount: 0,
  className: null
};
